import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import useApiResolver from "../../hooks/useApiResolver";
import CustomButton from "../../components/CustomButton";
import {
  paid_MoneyOptions,
  paymentPeriodOptions,
} from "../../helpers/Constants";
import ScrollTop from "../../helpers/ScrollTop";
import FinanceCalcResult from "./FinanceCalcResult";
import i18next from "i18next";

function FinanceCalcForm({
  JobPlacesOptions,
  bankOptions,
  car_id,
  modelVariationId,
  price,
  setIsLoading,
}) {
  const { t } = useTranslation();
  const isEnglish = i18next.language === "en";
  const formValues = useMemo(
    () => ({
      car_id,
      price,
      modelVariationId,

      customer_name: "",
      email: "",
      mobile: "",
      payment_period: "",
      month_payment: 0,
      paid_money: "",
      salary: 0,
      commitments: 0,
      lastpayment: 0,
      JobPlace_id: "",
      jobTypes_id: "",
      bank_id: "",
      remarks: "",
    }),
    [car_id, price],
  );

  const {
    formState: { errors },
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
  } = useForm({
    defaultValues: formValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const {
    resolveApi,
    isLoading,
    data: resultData,
    isSuccess,
  } = useApiResolver();

  const [financeCalcResult, setFinanceCalcResult] = useState(null);
  const {
    resolveApi: getJobTypes,
    data: JobTypes,
    isLoading: JobTypes_IsLoading,
  } = useApiResolver();

  const selectedJobPlace = watch("JobPlace_id");
  const JobTypesOptions = useMemo(
    () =>
      (JobTypes ?? []).map((Jobtype) => ({
        value: Jobtype.id,
        label: isEnglish ? Jobtype.nameEn : Jobtype.nameAr,
      })),
    [JobTypes, isEnglish],
  );

  const moneyRules = {
    min: {
      value: 0,
      message: t("form.invalidNumber"),
    },
    validate: (value) =>
      value === "" || Number.isFinite(Number(value)) || t("form.invalidNumber"),
  };

  const mobileRules = {
  validate: (value) => {
    if (!value) return true;

    return (
      /^(?:\+966|966|0)?5\d{8}$/.test(value) ||
      t("form.invalidNumber")
    );
  },
};

  useEffect(() => {
    setIsLoading(isLoading || JobTypes_IsLoading);
  }, [JobTypes_IsLoading, isLoading, setIsLoading]);

  useEffect(() => {
    if (isSuccess) {
      reset(formValues);
    }
  }, [formValues, isSuccess, reset]);

  useEffect(() => {
    if (resultData?.month_payment) {
      setFinanceCalcResult(resultData);
    }
  }, [resultData]);

  const handleJobChange = async (id) => {
    setValue("jobTypes_id", "");

    if (!id) {
      return;
    }

    getJobTypes({
      path: `/Banks/GetAllJobTypes/${id}`,
      errMeg: t("common.requestFailed"),
    });
  };

  const handleFinanceCalc = async (data) => {
    await resolveApi({
      path: "/car_finance_orders/AddOne",
      method: "post",
      body: data,
      errMeg: t("common.requestFailed"),
    });
  };

  const Field = ({ label, optional = false, children, fullWidth = false }) => (
    <div
      className={`finance-calc-field uk-margin-bottom${
        fullWidth ? " uk-width-1-1" : ""
      }`}
    >
      <label className="uk-form-label uk-text-bold uk-text-primary">
        {label}
        {optional && (
          <span className="finance-calc-optional">({t("form.optional")})</span>
        )}
      </label>
      <div className="uk-form-controls uk-margin-small-top">{children}</div>
    </div>
  );

  const TextField = ({ label, name, optional, fullWidth, ...inputProps }) => (
    <Field label={label} optional={optional} fullWidth={fullWidth}>
      <CustomInput
        control={control}
        name={name}
        error={errors[name]}
        required={!optional}
        {...inputProps}
      />
    </Field>
  );

  const SelectField = ({
    label,
    name,
    options,
    onChange,
    isDisabled,
    isLoading,
  }) => (
    <Field label={label}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <CustomSelect
            field={field}
            options={options}
            onChange={onChange}
            isDisabled={isDisabled}
            isLoading={isLoading}
            error={errors[name]}
          />
        )}
        rules={{
          required: t("form.required"),
        }}
      />
    </Field>
  );

  return (
    <>
      <ScrollTop key={financeCalcResult} />

      {financeCalcResult ? (
        <FinanceCalcResult
          financeCalcResult={financeCalcResult}
          setFinanceCalcResult={setFinanceCalcResult}
          setIsLoading={setIsLoading}
        />
      ) : (
        <form
          onSubmit={handleSubmit(handleFinanceCalc)}
          className="finance-calc-form uk-child-width-1-2@s uk-grid-small"
          uk-grid=""
          noValidate
        >
          <TextField
            label={t("form.name")}
            name="customer_name"
            optional
            autoComplete="name"
          />

        <TextField
  label={t("form.mobile")}
  name="mobile"
  
  inputMode="numeric"
  rules={mobileRules}
/>

          <TextField
            label={t("form.email")}
            name="email"
            type="email"
            optional
            autoComplete="email"
            rules={{
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t("form.invalidNumber"),
              },
            }}
          />

          <SelectField
            label={t("form.bank")}
            name="bank_id"
            options={bankOptions}
          />

          <SelectField
            label={t("form.JobPlace")}
            name="JobPlace_id"
            options={JobPlacesOptions}
            onChange={handleJobChange}
          />

          <SelectField
            label={t("form.JobType", { defaultValue: t("form.brandType") })}
            name="jobTypes_id"
            options={JobTypesOptions}
            isLoading={JobTypes_IsLoading}
            isDisabled={!selectedJobPlace || JobTypes_IsLoading}
          />

          <SelectField
            label={`${t("form.paymentPeriod")} (${t("form.month")})`}
            name="payment_period"
            options={paymentPeriodOptions}
          />

          <TextField
            label={`${t("form.monthPayment")} (${t("common.currency")})`}
            name="month_payment"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            rules={moneyRules}
          />

          <SelectField
            label={`${t("form.paidMoney")} (${t("form.rate")})`}
            name="paid_money"
            options={paid_MoneyOptions}
          />

          <TextField
            label={`${t("form.salary")} (${t("common.currency")})`}
            name="salary"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            rules={moneyRules}
          />

          <TextField
            label={`${t("form.commitments")} (${t("common.currency")})`}
            name="commitments"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            rules={moneyRules}
          />

          <TextField
            label={`${t("form.lastPayment")} (${t("common.currency")})`}
            name="lastpayment"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            rules={moneyRules}
          />

          <TextField
            label={t("form.remarks")}
            name="remarks"
            optional
            textarea
            fullWidth
          />

          <div className="actions uk-margin-top uk-width-1-1 uk-text-center">
            <CustomButton text={t("financeCalc.calc")} />
          </div>
        </form>
      )}
    </>
  );
}

export default FinanceCalcForm;
